import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom/cjs/react-router-dom";
import { useDispatch } from "react-redux";
import ProjectHeader from "../../_shared/ProjectHeader/ProjectHeader";
import {
  EXECUTION_BLUEPRINT,
  EXECUTION_BLUEPRINT_LIVE_REVIEW,
  EXECUTION_BLUEPRINT_REVIEW,
  // The two WIG Connections screens. Navigated to by the step-routing effect
  // below, never by a click — see there for why that distinction is
  // load-bearing.
} from "../../../../data/RouteUrls";
import { HeaderContext } from "../../../../HeaderContext";
import { breadCrumbsArray } from "../../../../data/breadCrumbs";
import { setDynamicBreadcrumbs } from "../../_shared/_redux/ProjectActions";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  EditIcon,
  InfoIcon,
  TrashIcon,
  StatusPill,
} from "../../StrategyCanvas/StrategyCanvasHelpers";
import CanvasBand from "../../StrategyCanvas/CanvasBand/CanvasBand";
import SummaryGroups from "../../StrategyCanvas/SummaryPanel/SummaryPanel";
import { StandardTooltip } from "../../../shared/StandardTooltip/StandardTooltip";
// The Blueprint tab's grid. Shared byte-identically with the CEO app - see that
// file's header. NOT the async review's `ExecutionBlueprintBlueprintMode`, which
// fetches over REST: that endpoint 403s for a CEO, and a REST read is stale
// mid-meeting, so both live screens render the socket snapshot through this
// instead. The async screen still uses its own component, untouched.
import BlueprintModeGrid from "./_shared/BlueprintModeGrid";
// Add / remove a WIG — handoff #25 §2. Both live in `_shared/` and are
// byte-identical with the coach app's copies, because the same two affordances
// appear on the coach's side of this screen and on all three Live Review
// stages.
import AddWigRow, { canDeleteLiveReviewItem } from "./_shared/AddWigRow";
import DeleteWigDialog from "./_shared/DeleteWigDialog";
import { WIG_LABEL_MAX } from "./_shared/WigFieldset";
import ConfirmStepModal, {
  CONFIRM_BLUEPRINT_REVIEW,
} from "../ExecutionBlueprintWigConnections/ConfirmStepModal";
import useExecutionBlueprintStore, {
  useExecutionBlueprintStoreSelector,
} from "../_redux/ExecutionBlueprintStore";
import useExecutionBlueprintReviewStore, {
  useExecutionBlueprintReviewStoreSelector,
} from "../ExecutionBlueprintReview/_redux/ExecutionBlueprintReviewStore";
import {
  REVIEW_STATUS,
  LIVE_REVIEW_GATE,
} from "../_redux/ExecutionBlueprintUrls";
import liveReviewSocketStore, {
  useLiveReviewSocket,
  PARTICIPANT_ROLE,
  PARTICIPANT_ROLE_LABEL,
  REMARK_TYPE,
  LIVE_REVIEW_STEP,
  isWigsLocked,
} from "./_socket/LiveReviewSocketStore";
// ⚠️ No step-2 views are imported here, deliberately. WIG Connections is part
// of the live review but it is NOT part of this screen: it has its own designed
// screens (the building screen and the connector canvas with the arrows), and
// this screen NAVIGATES to them when the step broadcast says so. See the
// step-routing effect below.
import {
  buildLiveAgenda,
  agendaCounts,
  allRemarksDiscussed,
  // Same function the CEO app's copy of this screen uses, rather than this
  // repo's `getSectionColor`. Identical values, but sharing it is what stops the
  // two sides drifting into different band colours - see the note in that file.
  agendaSectionColor,
} from "./_socket/liveReviewAgenda";
import { redirectForStep } from "../_redux/liveReviewStepRoutes";
import LiveReviewInfoModal from "./LiveReviewInfoModal";
// The shared skeleton chip — one shimmer across every live-review screen.
import EbSkeleton from "./_shared/EbSkeleton";
import "./ExecutionBlueprintLiveReview.scss";

// ─────────────────────────────────────────────────────────────────────────────
// Execution Blueprint Live Review - the joint session the coach runs with the
// CEO after submitting their solo review.
//
// LIVE as of 2026-08-27. This screen was design-only for as long as the contract
// had nothing in it; `12-live-review-fe-handoff.md` shipped step 1
// ("finalize_blueprint") with a REST start and a `/live-review` socket
// namespace, so every affordance the old header comment listed as deliberately
// absent now exists and actually saves.
//
// What changed, and the one thing to understand before editing:
//
//   - THE SOCKET IS THE SOURCE OF TRUTH. The left column renders
//     `snapshot.sections` from `live_review_details`, not the cached
//     `GET :uniqueId/review` payload. The review payload is still fetched, but
//     only as a first paint while the handshake completes - so a slow socket
//     shows the blueprint rather than a skeleton. The moment the snapshot
//     lands it takes over completely.
//
//   - THE AGENDA COMES FROM `snapshot.remarks`, not from `buildReviewSummary`.
//     This is the single most important departure and `liveReviewAgenda.js`
//     explains it at length: the server owns which points are outstanding and
//     how many, because accepting an item can resolve a remark server-side. A
//     client-side derivation would let the two participants see different
//     numbers - and a different enabled/disabled state on Continue - during a
//     live meeting.
//
//   - EITHER PARTY CAN DO EVERYTHING. Edit an item's value, accept it, flag it
//     to-discuss, mark a remark discussed. Not coach-only: §3.5 calls the CEO
//     being able to set review status "a genuine divergence from the coach's
//     earlier solo review", and this screen is symmetric with the CEO's copy.
//
//   - NOTHING IS APPLIED OPTIMISTICALLY. §6 broadcasts every successful action
//     back to the actor as well as the other party, so the echo is what updates
//     the screen. See the socket store's `editItem` for why that matters here
//     specifically and why the async review's optimistic approach is wrong.
//
// Still deliberately absent, per §9: adding a NEW comment (only resolving what
// the coach already left), locking the blueprint (step 3's job, and there is no
// lock endpoint at all), and any notion of a step after this one - Continue
// answers `not_yet_available` by design and is rendered as reassurance.
//
// The gate is now server-backed: the coach can only be here if the blueprint's
// own state says live review is running. See the review store.
// ─────────────────────────────────────────────────────────────────────────────

const selectClients = (state) => state.clients;
const selectReviewByUniqueId = (state) => state.reviewByUniqueId;
const selectReviewLoading = (state) => state.reviewLoading;
const selectLiveReviewByUniqueId = (state) => state.liveReviewByUniqueId;

// Socket-store selectors. Module scope, per CLAUDE.md - the hook resubscribes on
// selector identity, so an inline arrow would resubscribe every render, and this
// store pushes far more updates than a REST-backed one.
const selectSnapshot = (state) => state.snapshot;
const selectJoined = (state) => state.joined;
const selectJoining = (state) => state.joining;
const selectJoinError = (state) => state.joinError;
const selectLastError = (state) => state.lastError;
// No `selectContinueState`: the store still records `not_yet_available` in
// `continueState`, but this screen navigates away on Lock Blueprint so nothing
// here can render it. Re-add alongside a banner if the flow ever stays put.
const selectPendingItems = (state) => state.pendingItems;
const selectAddPending = (state) => state.addPending;
// No `selectConnectorPending` here: connector mutations happen on the WIG
// Connections screen, which subscribes to it itself. This screen only needs to
// know it is no longer the one in charge.
const selectLocking = (state) => state.locking;
const selectResnapshotRequests = (state) => state.resnapshotRequests;

const LIVE_REVIEW_INFO_SEEN_KEY = "ebLiveReviewInfoSeen";

// The steps this screen is the right home for. Everything else routes away via
// the shared table — see `_redux/liveReviewStepRoutes.js`.
const OWN_STEPS = [LIVE_REVIEW_STEP.FINALIZE_BLUEPRINT];

// ⚠️ Uses the shared `EbSkeleton` chip now (2026-09-03) rather than a local
// `.eblr-skel` paint rule — one shimmer across every live-review screen, see
// `_shared/EbSkeleton.scss`. Only the geometry classes below are this screen's.
//
// Row counts vary per band on purpose: the real canvas bands hold different
// numbers of items, and two identical stacks of three read as a placeholder
// graphic where uneven ones read as structure that has not loaded.
const SKELETON_BANDS = [3, 2];

const LiveReviewSkeleton = () => (
  <div className="eblr-body" aria-hidden="true">
    <div className="eblr-canvas">
      {SKELETON_BANDS.map((rows, band) => (
        // eslint-disable-next-line react/no-array-index-key
        <div className="eblr-skel-band" key={band}>
          <EbSkeleton className="eblr-skel-title" i={band} strong />
          {Array.from({ length: rows }).map((_, row) => (
            // eslint-disable-next-line react/no-array-index-key
            <EbSkeleton className="eblr-skel-row" key={row} i={band + row} />
          ))}
        </div>
      ))}
    </div>
    <div className="eblr-agenda">
      <div className="eblr-agenda-header">
        <EbSkeleton className="eblr-skel-agenda-title" strong />
      </div>
      <div className="eblr-agenda-body">
        {[0, 1, 2].map((card) => (
          <EbSkeleton className="eblr-skel-agenda-card" key={card} i={card} />
        ))}
      </div>
    </div>
  </div>
);

// Who's in the room. §7: `participants` is always a 2-item array with both
// roles present even before either has joined, so this renders two rows
// unconditionally and never has to handle a missing slot.
//
// The ~60s note is the handoff's own (§7): a hard network drop takes that long
// to flip `connected`, because it's the connection keepalive rather than an
// event. So this must never be presented as authoritative to the second - hence
// "was here" rather than a timestamp, and no "left 3s ago" style precision.
const PresenceStrip = ({ participants, viewerRole }) => (
  <div className="eblr-presence">
    {(participants || []).map((slot) => {
      const known = slot.name || (slot.user_id != null ? "Joined" : null);
      const isViewer = slot.role === viewerRole;
      return (
        <span
          className={`eblr-presence-chip${
            slot.connected ? " is-online" : " is-offline"
          }`}
          key={slot.role}
          title={
            slot.connected
              ? `${PARTICIPANT_ROLE_LABEL[slot.role]} is in the session`
              : known
                ? `${PARTICIPANT_ROLE_LABEL[slot.role]} was here but isn't connected right now`
                : `${PARTICIPANT_ROLE_LABEL[slot.role]} hasn't joined yet`
          }
        >
          <span className="eblr-presence-dot" />
          <span className="eblr-presence-role">
            {PARTICIPANT_ROLE_LABEL[slot.role]}
            {isViewer ? " (you)" : ""}
          </span>
          {/* Once we know who someone is we keep saying so, even offline -
              "Coach — Jane — offline" is useful where "Unknown" is not. §7
              asks for exactly this. */}
          {known && !isViewer && (
            <span className="eblr-presence-name">{known}</span>
          )}
          {/* ⚠️ THE STATE TEXT IS VISUALLY HIDDEN, NOT DELETED (2026-09-03,
              on instruction: "we do not want to show that text"). The DOT
              carries the state now — green when connected, red when not.

              It is clipped rather than removed because the colour of a 7px dot
              is the entire signal otherwise, and that leaves two groups with
              nothing: a screen reader gets no state at all, and a
              red/green-colourblind reader gets two dots they cannot tell
              apart. Clipped text stays in the accessibility tree, so the
              chip still announces "Coach Jane offline".

              The `title` above keeps the fuller nuance for a sighted user —
              in particular it is the only thing left distinguishing "was here
              but dropped" from "hasn't joined yet", since both now show the
              same red dot. */}
          <span className="eblr-presence-state">
            {slot.connected ? "online" : known ? "offline" : "not joined"}
          </span>
        </span>
      );
    })}
  </div>
);

// One blueprint item, now EDITABLE by either party.
//
// This was deliberately read-only while there was no endpoint - the old comment
// said an affordance that appears to save and doesn't is worse than none. That
// reasoning is unchanged; what changed is that these three affordances now save
// for real (§3.4-3.5), and both parties get them.
//
// ── The action model is Strategy Canvas's live-review row, deliberately ──────
// `StrategyCanvasLiveReviewWaiting.js`'s `LiveReviewRow` is the reference, and
// this now matches it affordance for affordance:
//
//   - **No "To discuss" button.** In a live session the two of them are already
//     discussing it; flagging it for later is what the SOLO review was for. The
//     status still arrives as `to_discuss` from that review and still renders as
//     a pill — it just can't be newly set from here.
//   - **"Finalise this block" only while it isn't accepted yet** (Canvas:
//     `status !== REVIEW_STATUS.ACCEPTED`). There is no "unset back to pending"
//     in the contract, so an accepted row has nothing left to set. The label is
//     Canvas's own wording, and it is the right wording for a joint session:
//     this is the coach and the CEO agreeing the item together, not the coach
//     signing off on someone else's work the way the solo review's "Accept"
//     was. It still emits `status: "accepted"` — only the label changed, so
//     nothing about the wire contract moves with it.
//
//     ⚠️ **Finalising also resolves the row's open remarks, in the SAME click**
//     (2026-08-31). This is the fix for a two-step flow that was wrong: a row
//     that came out of the solo review flagged `to_discuss` WITH a comment used
//     to show Finalise, and then — once finalising had flipped it to accepted —
//     grow a second "Mark discussed" button, so settling one item took two
//     clicks with a button appearing under the cursor between them. One action
//     in the conversation ("we're agreed on this one") must be one click.
//
//     An earlier version of this comment argued the opposite, on the grounds
//     that §6 says `remarks_updated` "always" accompanies `item_status_updated`
//     "because accepting an item can silently shrink the remarks list" — so the
//     server would resolve them and emitting our own would race it. That
//     inference was wrong: the server shrinks the list for remarks its own
//     accept resolves, and it demonstrably leaves these ones open, which is
//     exactly why the second button used to appear. See `onFinalise`.
//   - **Modify on every row, always** — the one control that is never
//     conditional, because agreeing new wording is the main thing this screen is
//     for and it must be reachable whatever state the item is in.
//   - **Mark discussed ONLY on a row that arrived already accepted with a
//     comment, and still has an open remark** — Canvas's `canMarkComplete`,
//     same three conditions. That is now the button's whole remaining purpose:
//     a row the solo review accepted while leaving a note has nothing to
//     finalise (it is already accepted) but still has a point to talk through,
//     so marking it discussed is the only action it needs — one button, one
//     click, one event. A row flagged `to_discuss` never reaches this branch,
//     because finalising it resolves its remarks in the same emit.
//
// Editing is now an explicit Modify → textarea → Discard/Save cycle rather than
// click-to-edit-and-save-on-blur. §3.4 says "no confirm step, no save button",
// and that was the basis for the old model — but Canvas's live review does have
// explicit save, and it is the safer of the two here for a reason §3.4 doesn't
// consider: this textarea is being typed into in front of the CEO, and a stray
// click away used to broadcast a half-written sentence to their screen as a
// committed edit. Discard exists so a change can be abandoned.
const LiveReviewRow = ({
  item,
  expanded,
  onToggle,
  onEditValue,
  onFinalise,
  onMarkDiscussed,
  remarksByItemId,
  pending,
  // §6a: once "Lock Blueprint" has fired, item values are permanently frozen and
  // the server answers `wigs_locked` to any edit. The handoff says "don't let
  // the UI even offer editing anymore once you're past this point", so every
  // action in this row disappears rather than being disabled — a disabled button
  // implies a state you could get back to, and there is no unlock.
  //
  // ⚠️ This now only goes true at the TERMINAL lock — handoff #25 §2 redefined
  // `isWigsLocked`, which used to mean "past the finalize step". Through all
  // three Live Review stages the row stays fully editable, which is what the
  // backend always allowed and the old step-based predicate was preventing.
  locked = false,
  // False when removing this WIG would take its section below the floor of one.
  canDelete = false,
  onDelete,
}) => {
  const status = item.review_status;
  // Memoised, not a bare `item.comments || []`. The fallback allocates a new
  // array on every render, which would make it a changing dependency of the
  // `openRemarks` memo below and recompute that on every render - i.e. the memo
  // would do nothing except cost an allocation. This is a live screen taking
  // socket pushes, so its rows re-render often.
  const comments = useMemo(() => item.comments || [], [item.comments]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.value || "");
  // E27 (Wayne, UAT #37/#90: "we can change the description but not the short
  // name"). Modify edits the WIG's name as well as its wording.
  const [labelDraft, setLabelDraft] = useState(item.label || "");

  // Re-sync the draft when the value changes underneath us - which happens
  // whenever the OTHER party edits this item. Skipped while this user is
  // actively editing: overwriting someone mid-sentence because their
  // counterpart typed is worse than the two briefly disagreeing, and Discard or
  // Save will resolve it either way.
  useEffect(() => {
    if (!editing) {
      setDraft(item.value || "");
      setLabelDraft(item.label || "");
    }
  }, [item.value, item.label, editing]);

  const beginEdit = () => {
    setDraft(item.value || "");
    setLabelDraft(item.label || "");
    setEditing(true);
  };

  const handleDiscard = () => {
    setDraft(item.value || "");
    setLabelDraft(item.label || "");
    setEditing(false);
  };

  const handleSave = () => {
    setEditing(false);
    const next = draft.trim();
    const nextLabel = labelDraft.trim();
    const valueChanged = next !== (item.value || "").trim();
    const labelChanged = nextLabel !== (item.label || "").trim();
    // Unchanged means no emit. Worth guarding: every successful edit broadcasts
    // to both screens and carries an "edited by" attribution, so a no-op save
    // would tell the other person something happened when nothing did.
    if (!valueChanged && !labelChanged) return;
    // `value` always travels (it is the field the event requires); `label`
    // only when it changed — see the store's `editItem`.
    onEditValue(
      item.execution_blueprint_item_id,
      next,
      labelChanged ? nextLabel : undefined,
    );
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSave();
    }
    if (event.key === "Escape") handleDiscard();
  };

  // Every remark still open against this row - the item's own, plus one per
  // flagged comment. Collected rather than counted so `canMarkDiscussed` and the
  // click handler read the same list, and so the id emitted is always a
  // REMARK's id and never an item's.
  const openRemarks = useMemo(() => {
    const found = [];
    const itemRemark =
      remarksByItemId[String(item.execution_blueprint_item_id)];
    if (itemRemark && !itemRemark.discussed) found.push(itemRemark);
    comments.forEach((comment) => {
      const remark = remarksByItemId[`c${comment.coach_comment_id}`];
      if (remark && !remark.discussed) found.push(remark);
    });
    return found;
  }, [remarksByItemId, item.execution_blueprint_item_id, comments]);

  // Canvas's `canMarkComplete`, condition for condition.
  const canMarkDiscussed =
    status === REVIEW_STATUS.ACCEPTED &&
    comments.length > 0 &&
    openRemarks.length > 0;

  return (
    <div
      className={`eblr-row ${expanded ? "expanded" : ""}${
        pending ? " is-pending" : ""
      }`}
      data-agenda-target={`item-${item.execution_blueprint_item_id}`}
    >
      <button className="eblr-row-header" onClick={onToggle}>
        <span className="eblr-row-left">
          <span className="eblr-row-label">{item.label}</span>
        </span>
        <span className="eblr-row-right">
          {status && (
            <StatusPill
              accepted={status === REVIEW_STATUS.ACCEPTED}
              className="eblr-status-pill"
              acceptedClassName="eblr-status-accepted"
              discussClassName="eblr-status-discuss"
            />
          )}
          <span className="eblr-row-chevron">
            {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </span>
        </span>
      </button>

      {expanded && (
        <div className="eblr-row-body">
          {editing ? (
            <div className="eblr-row-edit">
              {/* Same field names as the Add-a-WIG form (`WigFieldset`), so a
                  WIG is described in one set of words wherever it is edited. */}
              <label
                className="eblr-row-edit-label"
                htmlFor={`eblr-name-${item.execution_blueprint_item_id}`}
              >
                Name
              </label>
              <input
                id={`eblr-name-${item.execution_blueprint_item_id}`}
                className="eblr-row-editor eblr-row-editor--name"
                type="text"
                value={labelDraft}
                maxLength={WIG_LABEL_MAX}
                onChange={(event) => setLabelDraft(event.target.value)}
                onKeyDown={handleKeyDown}
                disabled={pending}
                /* Focus on mount is a direct response to the click that opened
                   the editor, not focus-stealing on page load - which is what
                   the a11y rule is about. */
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
              <label
                className="eblr-row-edit-label"
                htmlFor={`eblr-goal-${item.execution_blueprint_item_id}`}
              >
                The goal
              </label>
              <textarea
                id={`eblr-goal-${item.execution_blueprint_item_id}`}
                className="eblr-row-editor"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={handleKeyDown}
                rows={3}
                disabled={pending}
              />
              <div className="eblr-row-edit-actions">
                <button
                  type="button"
                  className="eblr-act-btn is-discard"
                  onClick={handleDiscard}
                  disabled={pending}
                >
                  Discard
                </button>
                <button
                  type="button"
                  className="eblr-act-btn is-save"
                  onClick={handleSave}
                  disabled={pending || !draft.trim() || !labelDraft.trim()}
                >
                  <CheckIcon size={12} /> {pending ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          ) : (
            <p className="eblr-row-value">
              {item.value || (
                <span className="eblr-row-empty">Not added yet</span>
              )}
            </p>
          )}

          {/* Attribution for the last edit. Only shown when we could resolve
              who did it - the payload field isn't documented, so the store
              falls back to matching a user id against the participants list
              and may still come back empty. Better silent than wrong about
              who changed the CEO's blueprint. */}
          {item._updatedByRole && (
            <p className="eblr-row-attribution">
              Edited by {PARTICIPANT_ROLE_LABEL[item._updatedByRole]}
            </p>
          )}

          {/* ⚠️ NO "Why:" LINE (2026-09-03, on instruction: removed "from every
                        block"). This rendered `item.rationale`.

                        `rationale` is still on the item, still round-trips through every
                        edit, and is still what the agent writes when it proposes a WIG —
                        nothing has been dropped from the data. It is simply no longer
                        printed on this surface. The CEO's own draft cards keep it, moved
                        behind a tooltip on the same pass.

                        The reasoning that applies everywhere it went: a rationale is
                        read once, while deciding whether to keep a WIG. Printed under
                        every item it doubled each row's height and pushed the values —
                        the things actually being compared — apart. */}

          {/* Read-only transcript boxes, as Canvas renders them. The
              per-comment mark-discussed buttons that used to live here are gone:
              there is now ONE discussed control per row, in the actions below,
              and it clears every remark still open against the row. Several
              buttons all called "Mark discussed", each resolving a different
              invisible remark, gave no way to tell which was which. A comment
              whose remark is already resolved still says so. */}
          {comments.length > 0 && (
            <div className="eblr-transcripts">
              {comments.map((comment, index) => {
                const remark = remarksByItemId[`c${comment.coach_comment_id}`];
                return (
                  <div
                    className="eblr-transcript"
                    key={comment.coach_comment_id ?? index}
                    data-agenda-target={
                      remark
                        ? `item-${item.execution_blueprint_item_id}`
                        : undefined
                    }
                  >
                    <span className="eblr-transcript-badge">Coach comment</span>
                    <p>{comment.comment_text}</p>
                    {remark && remark.discussed && (
                      <span className="eblr-discussed-tag">
                        <CheckIcon size={11} /> Discussed
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Hidden while editing: Discard and Save are the only two actions
              that make sense mid-edit, and they're in the editor. Canvas hides
              the whole non-editing branch for the same reason.

              See the block comment on this component for why there is no
              To-discuss button and why Modify is the only unconditional one. */}
          {!editing && !locked && (
            <div className="eblr-row-actions">
              {status !== REVIEW_STATUS.ACCEPTED && (
                <button
                  type="button"
                  className="eblr-act-btn is-accept"
                  /* Accepts the item AND resolves whatever remarks are still open
                   against it, in one click - `openRemarks` is passed rather
                   than re-derived so the ids emitted are the same list this row
                   is looking at. See the block comment above for why this is
                   one action and not two. */
                  onClick={() =>
                    onFinalise(item.execution_blueprint_item_id, openRemarks)
                  }
                  disabled={pending}
                >
                  <CheckIcon size={12} />{" "}
                  {pending ? "Finalising…" : "Finalise this WIG"}
                </button>
              )}

              {/* The only row that reaches this branch is one that arrived from
                the solo review ALREADY accepted with a comment against it:
                nothing to finalise, but a point still to talk through. So this
                is its single action, and clicking it emits only
                `mark_remark_discussed`.

                A `to_discuss` row can no longer land here — Finalise resolves
                its remarks in the same click, which is what stopped this button
                appearing as a surprise second step. It remains as the honest
                fallback if that emit ever fails: the remarks stay open, the row
                is accepted, and this reappears so the point can still be
                settled by hand. */}
              {canMarkDiscussed && (
                <button
                  type="button"
                  className="eblr-act-btn is-discussed"
                  /* Explicit arrow, not a bare `forEach(onMarkDiscussed)` -
                   forEach would pass (remark, index, array) and the extra args
                   are only harmless by luck. Several emits in one tick is fine:
                   socket.io queues them, and unlike Canvas's store there is no
                   in-flight guard here to drop the second one. */
                  onClick={() =>
                    openRemarks.forEach((remark) => onMarkDiscussed(remark))
                  }
                  disabled={pending}
                >
                  <CheckIcon size={12} /> Mark discussed
                </button>
              )}

              <button
                type="button"
                className="eblr-act-btn is-modify"
                onClick={beginEdit}
                disabled={pending}
              >
                <EditIcon size={12} /> Modify
              </button>

              {/* ── Remove ──
                Last in the row and visually quietest, which is the whole
                placement argument: it is the only irreversible action here, and
                the three beside it are the ones this screen is actually for.
                Putting it first — or giving it the same weight — would make the
                destructive option the easiest one to hit while working down a
                list at conversational speed.

                Hidden rather than disabled at the floor. A section must keep
                one WIG, and the last row's Remove would otherwise be a dead
                control on every single-item section; the composer's own cap
                note is where bounds get explained, not here. */}
              {canDelete && (
                <button
                  type="button"
                  className="eblr-act-btn is-remove"
                  onClick={onDelete}
                  disabled={pending}
                >
                  <TrashIcon size={12} /> Remove
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Local rather than imported from `ExecutionBlueprintReview.js`, which also
// exports a pair by these names. They are private tab state on each screen, not
// a shared contract, and importing that screen here for two strings would pull a
// whole route component in and set up a cycle the first time anything over there
// wanted to import this one.
const MODE_REVIEW = "review";
const MODE_BLUEPRINT = "blueprint";

const ExecutionBlueprintLiveReview = () => {
  const history = useHistory();
  const { blueprintId } = useParams();
  const dispatch = useDispatch();

  const [mode, setMode] = useState(MODE_REVIEW);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [expandedItemId, setExpandedItemId] = useState(null);
  // The WIG awaiting delete confirmation, or null. Holds the whole item, not
  // its id: the dialog quotes the wording back, and looking it up again at
  // render time would race the very deletion it is confirming.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState(() => new Set());
  const [pendingTarget, setPendingTarget] = useState(null);
  const canvasRef = useRef(null);

  const clients = useExecutionBlueprintStoreSelector(selectClients);
  const client = (clients || []).find((c) => c.blueprintId === blueprintId);
  const companyName = client?.name || "";

  const reviewByUniqueId = useExecutionBlueprintReviewStoreSelector(
    selectReviewByUniqueId,
  );
  const reviewLoading =
    useExecutionBlueprintReviewStoreSelector(selectReviewLoading);
  // Subscribed to rather than read once, so the gate effect below re-runs when
  // a start lands or the review payload arrives.
  const liveReviewByUniqueId = useExecutionBlueprintReviewStoreSelector(
    selectLiveReviewByUniqueId,
  );
  const review = reviewByUniqueId[blueprintId];

  const snapshot = useLiveReviewSocket(selectSnapshot);
  const joined = useLiveReviewSocket(selectJoined);
  const joining = useLiveReviewSocket(selectJoining);
  const joinError = useLiveReviewSocket(selectJoinError);
  const lastError = useLiveReviewSocket(selectLastError);
  const pendingItems = useLiveReviewSocket(selectPendingItems);
  const addPending = useLiveReviewSocket(selectAddPending);
  const locking = useLiveReviewSocket(selectLocking);
  const resnapshotRequests = useLiveReviewSocket(selectResnapshotRequests);

  // ── The gate ──────────────────────────────────────────────────────────────
  // A coach who types this URL, bookmarks it, or arrives via history hasn't
  // started anything, so they get sent back to the review screen where the Start
  // Live Review button lives. `replace` rather than `push` so Back doesn't
  // bounce them straight into the same redirect.
  //
  // ⚠️ TRI-STATE, and that is the whole point. This used to be a boolean read
  // out of localStorage, which answers synchronously on the very first render. A
  // server-backed gate cannot: the review payload may not have arrived yet. A
  // boolean would read `false` during that window and redirect a coach off a
  // screen they are entitled to be on - so UNKNOWN has to mean "wait", and only
  // an explicit NOT_STARTED redirects.
  //
  // The dashboard's stage is passed in as a second source because it's often
  // loaded when the review isn't, and it names the same fact in a different
  // vocabulary (`LIVE_REVIEW_STARTED` vs `LIVE_REVIEW_IN_PROGRESS`).
  //
  // The resolver reads both of its sources out of the store rather than taking
  // them as arguments, so the two subscribed values below are what make this
  // reactive: without them in the dep list this would resolve once, at first
  // render, when the answer is usually UNKNOWN — and never re-resolve.
  const gate = useMemo(
    () =>
      useExecutionBlueprintReviewStore
        .getState()
        .resolveLiveReviewGate(blueprintId, { dashboardStage: client?.stage }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [blueprintId, client?.stage, reviewByUniqueId, liveReviewByUniqueId],
  );

  useEffect(() => {
    if (gate === LIVE_REVIEW_GATE.NOT_STARTED) {
      history.replace(
        EXECUTION_BLUEPRINT_REVIEW.replace(":blueprintId", blueprintId),
      );
    }
    // eslint-disable-next-line
  }, [blueprintId, gate]);

  // ── Socket lifecycle ──────────────────────────────────────────────────────
  // Connected only once the gate says the session is actually live. Joining
  // before the server has started it fails with `live_review_not_started` (§8)
  // and there is nothing useful to do with that error, so this waits instead of
  // emitting a request it knows will be rejected.
  //
  // `connect` is idempotent (the store guards on `listenersBound` and strips
  // every listener before rebinding), which is what makes StrictMode's double
  // mount harmless here without any StrictMode-specific code.
  useEffect(() => {
    if (gate !== LIVE_REVIEW_GATE.STARTED || !blueprintId) return undefined;
    liveReviewSocketStore.getState().connect({
      uniqueId: blueprintId,
      viewerRole: PARTICIPANT_ROLE.COACH,
    });
    return () => liveReviewSocketStore.getState().disconnect();
  }, [gate, blueprintId]);

  // §8 asks for a snapshot refresh on `item_not_found` / `remark_not_found`, and
  // the store also asks for one when a delta arrives for an item we don't hold.
  // Re-joining is the refresh: `join_live_review` is what returns a full
  // `live_review_details`, and there is no separate "resend the snapshot" event.
  //
  // Guarded on `> 0` so it doesn't fire on mount alongside the join above.
  useEffect(() => {
    if (resnapshotRequests > 0) liveReviewSocketStore.getState().join();
  }, [resnapshotRequests]);

  const HeaderMetaData = useContext(HeaderContext);
  useEffect(() => {
    document.title = "Art of Scale | Execution Blueprint Live Review";
    HeaderMetaData.setHeaderMetadata({ headerTitle: "Execution Blueprint" });
    if (!useExecutionBlueprintStore.getState().clients) {
      useExecutionBlueprintStore.getState().fetchDashboard();
    }
    // The review payload is normally already cached from the review screen the
    // coach just came from; this covers a hard reload of this URL after the
    // session has legitimately been started.
    useExecutionBlueprintReviewStore.getState().fetchReview(blueprintId);
    // eslint-disable-next-line
  }, [blueprintId]);

  // ── "Execution Blueprint › ‹Company› Live Review" ─────────────────────────
  // Exactly TWO crumbs. The company and the stage are ONE label, not two
  // crumbs — "Acme Ltd Live Review" — because they name a single thing: this
  // client's live session. Splitting them made the trail read as a hierarchy
  // with a level you could climb to, which is what was misleading.
  //
  //   - **Execution Blueprint** — linked to the coach's client dashboard. The
  //     one legitimate way out of a live session, so it keeps its url.
  //   - **‹Company› Live Review** — inert. The current screen.
  //
  // ⚠️ There is NO "Blueprint Review" crumb. Removed on instruction: a trail
  // showing a path back to the async review implies the coach can return to it,
  // and they can't once the live review has started.
  //
  // Falls back to a bare "Live Review" before the dashboard lands, rather than
  // a leading space or an empty crumb drawing a stray separator.
  useEffect(() => {
    dispatch(
      setDynamicBreadcrumbs(EXECUTION_BLUEPRINT_LIVE_REVIEW, [
        breadCrumbsArray[EXECUTION_BLUEPRINT][0],
        {
          title: companyName ? `${companyName} Live Review` : "Live Review",
          url: "",
        },
      ]),
    );
    // eslint-disable-next-line
  }, [companyName, blueprintId]);

  useEffect(() => {
    try {
      if (!localStorage.getItem(LIVE_REVIEW_INFO_SEEN_KEY)) {
        setShowInfoModal(true);
        localStorage.setItem(LIVE_REVIEW_INFO_SEEN_KEY, "true");
      }
    } catch (error) {
      // Storage unavailable - skip the one-time explainer rather than break
      // the screen. It's still reachable from the header.
    }
  }, []);

  // ── The live tree ─────────────────────────────────────────────────────────
  // The socket snapshot wins whenever it exists. The review payload is a
  // FIRST-PAINT FALLBACK only: it makes the screen show the blueprint while the
  // handshake completes instead of a skeleton, and it is dropped the instant
  // `live_review_details` lands.
  //
  // Worth being explicit that these are not interchangeable. The review payload
  // has no `remarks`, no `participants` and no counters, so everything derived
  // from those degrades to empty while it's in use - which is correct (there is
  // no session yet to have an agenda for) but would be wrong if it persisted.
  const liveTree = snapshot || review || null;
  const sections = useMemo(
    () =>
      [...(liveTree?.sections || [])].sort(
        (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0),
      ),
    [liveTree],
  );

  // From the server's own remarks list - see `liveReviewAgenda.js` for why this
  // must not be derived from item statuses.
  const agendaGroups = useMemo(() => buildLiveAgenda(snapshot), [snapshot]);
  const counts = agendaCounts(snapshot);
  const agendaTotal = counts.total;

  // Two lookups so a row can find the remark it is the subject of without
  // re-deriving an id: items keyed by item id, comments keyed by `c<commentId>`.
  // Built here rather than in each row so it's one pass over remarks instead of
  // one per row.
  const remarksByItemId = useMemo(() => {
    const map = {};
    (snapshot?.remarks || []).forEach((remark) => {
      if (remark.remark_type === REMARK_TYPE.COMMENT) {
        map[`c${remark.id}`] = remark;
      } else if (remark.execution_blueprint_item_id != null) {
        map[String(remark.execution_blueprint_item_id)] = remark;
      }
    });
    return map;
  }, [snapshot]);

  const handleEditValue = (itemId, value, label) =>
    liveReviewSocketStore.getState().editItem(itemId, value, label);
  const handleMarkDiscussed = (remark) =>
    liveReviewSocketStore.getState().markRemarkDiscussed(remark);

  // ── Adding and removing WIGs (handoff #25 §2) ─────────────────────────────
  const handleAddItem = (sectionKey, draft) =>
    liveReviewSocketStore.getState().addLiveReviewItem(sectionKey, draft);

  // The dialog is mounted once at screen level rather than per row: only one
  // can be open, and a row that unmounts mid-confirm — which a socket push can
  // do at any moment on this screen — would take its own dialog with it.
  const handleConfirmDelete = () => {
    const id = pendingDelete?.execution_blueprint_item_id;
    if (id == null) return;
    liveReviewSocketStore.getState().deleteLiveReviewItem(id);
    setPendingDelete(null);
  };

  // How many connectors the WIG awaiting confirmation would take with it.
  // Counted from the graph we already hold rather than asked for, because the
  // dialog needs it before the delete happens — the server's own
  // `deleted_connector_ids` only arrives afterwards, which is too late to warn
  // anybody. It is a count for a sentence, not a source of truth: the actual
  // cascade is still whatever the server reports.
  const pendingDeleteConnections = useMemo(() => {
    const id = pendingDelete?.execution_blueprint_item_id;
    if (id == null) return 0;
    const key = String(id);
    return (snapshot?.connectors?.edges || []).filter(
      (edge) => String(edge.from) === key || String(edge.to) === key,
    ).length;
  }, [pendingDelete, snapshot]);

  // "Finalise this block" — ONE user action, two emits.
  //
  // Accepting the item and resolving its open remarks are one decision in the
  // conversation ("we're agreed on this one"), so they are one click. Splitting
  // them was the bug: a `to_discuss` row with a comment showed Finalise, and
  // then grew a Mark-discussed button once the accept landed.
  //
  // Order matters, mildly: the status goes first because it is the decision, and
  // the remarks follow because they are consequences of it. Both are
  // fire-and-forget on one socket, so socket.io delivers them in this order.
  //
  // `remarks` comes from the row rather than being re-derived here, so the ids
  // emitted are exactly the ones that row was showing as open — re-deriving
  // would risk emitting against a remark the row had already seen resolved.
  //
  // Not batched into a single server call because the contract has no such
  // event: §6 gives `set_item_review_status` and `mark_remark_discussed`
  // separately, and inventing a combined one would be inventing contract. Worth
  // asking backend for, since two round trips for one click is the kind of thing
  // that shows up as a flicker on the other person's screen.
  //
  // ⚠️ Must stay in step with the CEO app's copy — see D24. If one side
  // finalises in one click and the other takes two, the two people are working
  // different screens.
  const handleFinalise = (itemId, remarks) => {
    const store = liveReviewSocketStore.getState();
    store.setItemStatus(itemId, REVIEW_STATUS.ACCEPTED);
    (remarks || []).forEach((remark) => store.markRemarkDiscussed(remark));
  };

  // Clicking an agenda item opens its section and row, then scrolls to it.
  // The scroll happens in an effect rather than here because the row it's
  // scrolling to may not be mounted yet - it's inside a section this click
  // just expanded.
  const handleSelectAgendaItem = (item, group) => {
    // `group.key` is the section key in the shape `buildLiveAgenda` produces
    // (`sectionKey` was the old builder's field name). Both are checked so this
    // keeps working if either shape reaches it.
    const sectionKey = group?.key || group?.sectionKey;
    if (sectionKey) {
      setCollapsedSections((prev) => {
        if (!prev.has(sectionKey)) return prev;
        const next = new Set(prev);
        next.delete(sectionKey);
        return next;
      });
    }
    setExpandedItemId(item.id?.startsWith("item-") ? item.id : null);
    setPendingTarget(item.id);
  };

  useEffect(() => {
    if (!pendingTarget || !canvasRef.current) return;
    const target = canvasRef.current.querySelector(
      `[data-agenda-target="${pendingTarget}"]`,
    );
    setPendingTarget(null);
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    // A flash on arrival - the view otherwise just moves and the coach has to
    // work out which of several near-identical rows they were sent to.
    target.classList.add("eblr-target-flash");
    const timer = setTimeout(
      () => target.classList.remove("eblr-target-flash"),
      1600,
    );
    return () => clearTimeout(timer);
  }, [pendingTarget, expandedItemId, collapsedSections]);

  const toggleSection = (sectionKey) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionKey)) next.delete(sectionKey);
      else next.add(sectionKey);
      return next;
    });
  };

  // ── The Continue gate ─────────────────────────────────────────────────────
  // §3.7: available "once everything is discussed". That is the SERVER's
  // `remarks_remaining`, not a client-side count - the previous version of this
  // screen gated on "every item has a decision", which was the honest subset
  // available when there was no remarks concept at all. There is one now, and it
  // is authoritative: accepting an item can resolve a remark server-side, so a
  // local count would drift and the two participants could disagree about
  // whether Continue is available.
  // ── Step routing (§6a) ────────────────────────────────────────────────────
  // `live_review_step` is the screen router, not the click that got us here.
  // §6a.4: a re-`join` returns the current step and "that is what routes your
  // screen, not which broadcast you happened to catch" — so this is also what
  // makes a reload, or a reconnect after the agent finished, land in the right
  // place. `liveTree` is not used for this: only the socket snapshot carries a
  // step, and the cached REST review payload must never imply one.
  const step = snapshot?.live_review_step;
  const locked = isWigsLocked(snapshot);

  // ⚠️ The step NAVIGATES, it does not swap a panel. WIG Connections is part of
  // the live review but it has its own designed screens, so once the blueprint
  // is locked this screen's job is done and it hands over.
  //
  // Driven by the BROADCAST, never by the click that caused it: §6a sends
  // `live_review_step_changed` to both parties, so both are carried across
  // together. A local push on the confirm would move only whoever pressed it and
  // leave their counterpart on a screen that is no longer live. It also covers
  // the §6a.4 case — someone away when the agent finished gets no broadcast and
  // picks the step up from their next join.
  //
  // `replace`, not `push`: the finalize screen is not somewhere Back should
  // return to once the values are frozen.
  // One shared table now (`_redux/liveReviewStepRoutes.js`) rather than a
  // ladder per screen. The ladder this replaces had drifted: its last two
  // branches had become the same value, so the ternary chose between Year 1 and
  // Year 1 — harmless here, and exactly the kind of rot four hand-copied tables
  // produce.
  useEffect(() => {
    const target = redirectForStep(step, OWN_STEPS, blueprintId);
    if (target) history.replace(target);
  }, [step, blueprintId, history]);

  const canComplete = allRemarksDiscussed(snapshot) && joined && !locked;
  const completeDisabledReason = !joined
    ? "Connecting to the live session…"
    : counts.remaining > 0
      ? `${counts.remaining} ${
          counts.remaining === 1 ? "point" : "points"
        } still to discuss.`
      : "";

  // Lock Blueprint → emit, and then DO NOTHING locally.
  //
  // ⚠️ This is the §6a rewrite, and the "do nothing" is the point. Until
  // 2026-08-27 this handler pushed the coach to the WIG Connections loading
  // route itself. That is now wrong: §6a makes the lock broadcast
  // `live_review_step_changed` to BOTH parties, and the step is what routes the
  // screen. Navigating locally would move only the person who pressed the
  // button and leave their counterpart sitting on a finalize screen that is no
  // longer live — on a two-person screen that is the worst class of bug.
  //
  // It also has to be this way for the case §6a.4 describes: someone who was
  // disconnected when the agent finished gets no broadcast at all, and picks the
  // step up from their next `join_live_review`. Only step-routing handles both.
  //
  // ⚠️ The lock is now PERMANENT — it freezes every item value and there is no
  // undo and no unlock endpoint. The confirmation in front of it has to say so.
  const handleConfirmComplete = () => {
    setShowCompleteConfirm(false);
    liveReviewSocketStore.getState().submitFinalize();
    // The dashboard's stage may have moved; refresh it so a later return there
    // is accurate. Fire-and-forget.
    useExecutionBlueprintStore.getState().fetchDashboard();
  };

  // Skeleton only while we have nothing at all to draw. Once the review payload
  // has landed it paints, even before the socket joins - a live session opening
  // on a skeleton for the length of a handshake reads as broken, and the content
  // it would show is the same content the snapshot is about to confirm.
  const showSkeleton = !liveTree && (reviewLoading || joining);

  // ── Don't paint this screen until we know it IS this screen ────────────────
  // Live Review is three stages behind one entry point, and which stage you
  // belong on is decided by `live_review_step` — which arrives with the first
  // snapshot, a beat after mount.
  //
  // This screen used to render its finalize UI immediately and rely on the
  // step-routing effect above to bounce anyone whose step said otherwise. So a
  // coach or CEO opening Live Review mid-journey saw the blocks screen, then a
  // redirect to connectors or Year 1: two visible screens for one click, and the
  // first one wrong. That is the bug this guard closes.
  //
  // The rule is now simply "render only on my own step". Everything else — an
  // unresolved step included — is a loader, and the effect above does the
  // navigating. `!==` rather than a list of the other steps on purpose: a step
  // this build has never heard of should also wait rather than render the wrong
  // stage confidently.
  //
  // `joinError` breaks the wait. Without it a failed handshake would spin here
  // forever, and this is the one place that would be indistinguishable from a
  // slow agent.
  if (step !== LIVE_REVIEW_STEP.FINALIZE_BLUEPRINT) {
    return (
      <div>
        <ProjectHeader
          handleNavigation={() =>
            history.push(
              EXECUTION_BLUEPRINT_REVIEW.replace(":blueprintId", blueprintId),
            )
          }
          showBack
        />
        <div id="execution-blueprint-live-review" aria-busy={!joinError}>
          {joinError ? (
            <div className="eblr-banner is-error" role="alert">
              <span>{joinError.message}</span>
              <button
                type="button"
                className="eblr-banner-btn"
                onClick={() => liveReviewSocketStore.getState().join()}
              >
                Try again
              </button>
            </div>
          ) : (
            <LiveReviewSkeleton />
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <ProjectHeader
        handleNavigation={() =>
          history.push(
            EXECUTION_BLUEPRINT_REVIEW.replace(":blueprintId", blueprintId),
          )
        }
        showBack
      />
      <div id="execution-blueprint-live-review">
        <div className="eblr-header">
          <div className="eblr-header-left">
            <div className="eblr-header-title">
              Live Review{companyName ? ` — ${companyName}` : ""}
            </div>

            {/* Who's in the room, inline beside the title rather than on its own
                strip below the header — that strip cost ~32px of vertical space
                on a screen whose height is bounded and whose two columns both
                want every pixel. Only once the snapshot has landed: the review
                payload carries no participants, so rendering this off the
                fallback tree would show both people as "not joined" when the
                truth is that we don't know yet. */}
            {snapshot && (
              <PresenceStrip
                participants={snapshot.participants}
                viewerRole={PARTICIPANT_ROLE.COACH}
              />
            )}
          </div>
          <div className="eblr-header-right">
            <button
              type="button"
              className="eblr-info-icon"
              onClick={() => setShowInfoModal(true)}
            >
              <InfoIcon size={14} />
              <span>How live review works</span>
            </button>
            {liveTree ? (
              <div className="eblr-agenda-count">
                Agenda items:<strong> {agendaTotal}</strong>
              </div>
            ) : (
              <EbSkeleton className="eblr-skel-agenda-count" />
            )}

            {/* Same two-tab toggle as the coach's async review header, same
                class names (`.ebr-mode-*`, imported with that stylesheet) so the
                two screens cannot drift apart visually. Blueprint Mode here is
                the SAME component the async review renders, unchanged — see the
                note where it's rendered below for what that means for freshness. */}
            <div className="ebr-mode-toggle">
              <button
                type="button"
                className={`ebr-mode-btn ${
                  mode === MODE_REVIEW ? "active" : ""
                }`}
                onClick={() => setMode(MODE_REVIEW)}
              >
                Review Mode
              </button>
              <button
                type="button"
                className={`ebr-mode-btn ${
                  mode === MODE_BLUEPRINT ? "active" : ""
                }`}
                onClick={() => setMode(MODE_BLUEPRINT)}
              >
                Blueprint Mode
              </button>
            </div>
          </div>
        </div>

        {/* Connection trouble. Separate from action errors below: this one means
            nothing on the screen is live, which the coach needs to know before
            they start editing in front of the CEO. */}
        {joinError && (
          <div className="eblr-banner is-error" role="alert">
            <span>{joinError.message}</span>
            <button
              type="button"
              className="eblr-banner-btn"
              onClick={() => liveReviewSocketStore.getState().join()}
            >
              Try again
            </button>
          </div>
        )}

        {/* A rejected action. Dismissible, because most of these are transient
            and the screen stays usable. */}
        {lastError && (
          <div className="eblr-banner is-error" role="alert">
            <span>{lastError.message}</span>
            <button
              type="button"
              className="eblr-banner-btn"
              onClick={() => liveReviewSocketStore.getState().clearError()}
            >
              Dismiss
            </button>
          </div>
        )}

        {/* The `not_yet_available` reassurance banner that used to sit here is
            gone. It existed because Lock Blueprint (then "Continue") stayed on
            this screen and the coach needed to be told the emit had landed
            harmlessly. Now the lock hands both parties over to the WIG
            Connections screens on the step broadcast, so that reply arrives
            after this screen has unmounted and the banner could never be seen.
            (It is the BROADCAST that moves them, not the confirm — see the
            step-routing effect.) `continueState` is still set in the store, and
            still cleared on the next connect - nothing reads it here. */}

        <div className="eblr-header-divider" />

        {/* Blueprint Mode, fed from the LIVE snapshot rather than a REST read,
            so an item edited in this session shows its new value here
            immediately and the CEO's copy of this tab shows exactly the same
            thing. `liveTree` rather than `snapshot` so the tab still paints from
            the cached review payload during the handshake instead of flashing a
            skeleton; `loading` covers the case where neither has landed. */}
        {mode === MODE_BLUEPRINT ? (
          <BlueprintModeGrid
            sections={liveTree?.sections || []}
            bhag={liveTree?.bhag}
            loading={showSkeleton}
            footnote={
              locked
                ? "Read-only view of the locked Execution Blueprint. Item values can no longer be changed."
                : `Read-only view of ${
                    companyName ? `${companyName}'s` : "this"
                  } Execution Blueprint. Switch to Review Mode to edit items or finalise them.`
            }
          />
        ) : showSkeleton ? (
          <LiveReviewSkeleton />
        ) : (
          <div className="eblr-body">
            <div className="eblr-canvas" ref={canvasRef}>
              {sections.map((section, index) => {
                const items = section.items || [];
                const toDiscussCount = items.filter(
                  (item) => item.review_status === REVIEW_STATUS.TO_DISCUSS,
                ).length;

                return (
                  <div className="eblr-section" key={section.section_key}>
                    <CanvasBand
                      label={section.label}
                      color={agendaSectionColor(index)}
                      showProgress={false}
                      openCount={toDiscussCount}
                      expanded={!collapsedSections.has(section.section_key)}
                      onToggle={() => toggleSection(section.section_key)}
                    >
                      {/* ⚠️ One box PER section comment, each carrying its own
                          anchor. This block used to render a single box holding
                          every comment, anchored only to `comments[0]`'s id —
                          but `agendaTargetFor` emits `section-<remark.id>` for
                          each one, so clicking the second or third section
                          comment in the agenda found no target and scrolled
                          nowhere. The comments were on screen; the agenda just
                          couldn't reach them, which is indistinguishable from
                          their being missing.

                          The anchor is the COMMENT's id because a section remark
                          IS the comment — `liveReviewAgenda.agendaTargetFor`
                          uses `remark.id` for section-scoped remarks, and for
                          those the remark id and the comment id are the same
                          thing. Keep these two in step. */}
                      {(section.comments || []).map((comment, commentIndex) => (
                        <div
                          className="eblr-section-comment"
                          key={comment.coach_comment_id ?? commentIndex}
                          data-agenda-target={`section-${comment.coach_comment_id}`}
                        >
                          <span className="eblr-section-comment-badge">
                            Section comment
                          </span>
                          <p>{comment.comment_text}</p>
                        </div>
                      ))}

                      <div className="eblr-rows">
                        {items.map((item) => {
                          const itemId = `item-${item.execution_blueprint_item_id}`;
                          return (
                            <LiveReviewRow
                              key={item.execution_blueprint_item_id}
                              item={item}
                              expanded={expandedItemId === itemId}
                              onToggle={() =>
                                setExpandedItemId((prev) =>
                                  prev === itemId ? null : itemId,
                                )
                              }
                              onEditValue={handleEditValue}
                              onFinalise={handleFinalise}
                              onMarkDiscussed={handleMarkDiscussed}
                              remarksByItemId={remarksByItemId}
                              pending={
                                !!pendingItems[
                                  String(item.execution_blueprint_item_id)
                                ]
                              }
                              locked={locked}
                              canDelete={canDeleteLiveReviewItem(items.length)}
                              onDelete={() => setPendingDelete(item)}
                            />
                          );
                        })}

                        {/* Last child of the rows stack, not a sibling of it:
                            the row is the next slot in this list, and putting
                            it outside would separate it from the thing it adds
                            to by the stack's own padding. */}
                        <AddWigRow
                          sectionKey={section.section_key}
                          sectionLabel={section.label}
                          count={items.length}
                          pending={addPending === section.section_key}
                          onAdd={handleAddItem}
                          disabled={locked}
                        />
                      </div>
                    </CanvasBand>
                  </div>
                );
              })}

              {(liveTree?.overall_comments || []).length > 0 && (
                <div className="eblr-overall" data-agenda-target="overall">
                  <span className="eblr-overall-badge">Overall assessment</span>
                  {liveTree.overall_comments.map((comment, index) => (
                    <p
                      className="eblr-overall-text"
                      key={comment.coach_comment_id ?? index}
                    >
                      {comment.comment_text}
                    </p>
                  ))}
                </div>
              )}
            </div>

            <div className="eblr-agenda">
              <div className="eblr-agenda-header">Meeting Agenda</div>
              <div className="eblr-agenda-body">
                {agendaTotal === 0 ? (
                  <div className="eblr-agenda-empty">
                    <span className="eblr-agenda-empty-icon">
                      <CheckIcon size={18} />
                    </span>
                    <p className="eblr-agenda-empty-title">
                      Nothing to discuss
                    </p>
                    <p className="eblr-agenda-empty-text">
                      You accepted every item without flagging anything, so
                      there are no agenda items for this session.
                    </p>
                  </div>
                ) : (
                  <SummaryGroups
                    groups={agendaGroups}
                    onSelectItem={handleSelectAgendaItem}
                    selectTitle="Show this in the blueprint"
                  />
                )}
              </div>
              {/* The "n of m discussed" readout and its progress track were
                  removed on instruction. `counts` is still read — the Lock
                  Blueprint gate and its tooltip both use `counts.remaining`, and
                  the header still prints `counts.total` — so this is a display
                  removal only, not a data one. */}

              <div className="eblr-agenda-footer">
                {canComplete ? (
                  <button
                    type="button"
                    className="eblr-complete-btn"
                    onClick={() => setShowCompleteConfirm(true)}
                  >
                    Continue to WIG Connections
                    <ChevronRightIcon />
                  </button>
                ) : (
                  <StandardTooltip tooltTipContent={completeDisabledReason}>
                    <span className="eblr-complete-btn-wrap">
                      <button
                        type="button"
                        className="eblr-complete-btn"
                        disabled
                      >
                        Continue to WIG Connections
                        <ChevronRightIcon />
                      </button>
                    </span>
                  </StandardTooltip>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {showInfoModal && (
        <LiveReviewInfoModal onClose={() => setShowInfoModal(false)} />
      )}

      {/* The design's stage-1 popup — Figma node 33831:45780, built as
          `ConfirmStepModal`'s CONFIRM_BLUEPRINT_REVIEW preset for exactly this
          moment, and its "up next: (2) WIG Connections" block is now literally
          true.

          ⚠️ The subtitle is OVERRIDDEN here, and the override is the point.
          §6a.1 requires a confirmation that says the WIGs are now locked and
          nothing can be changed — "this is genuinely permanent, not a soft
          transition". The preset's own subtitle describes confirming a review,
          which was accurate when this action did nothing and is now the single
          most misleading sentence we could show: it is the last screen before an
          irreversible change. The preset keeps its copy for the other two
          stages; only this call site needs the warning. */}
      {/* ⚠️ THE SUBTITLE WAS WRONG AFTER HANDOFF #25 (corrected 2026-09-14). It
          said "this locks the blueprint permanently — item values can't be
          changed afterwards", which was true when written and is now the
          opposite of what happens: #25 §2 keeps every WIG editable through the
          whole of Live Review, and only the Year 1 confirmation is terminal.
          What this step actually does is end stage 1 and start the connectors
          agent, so that is what it now says. */}
      {showCompleteConfirm && (
        <ConfirmStepModal
          {...CONFIRM_BLUEPRINT_REVIEW}
          subtitle="This finishes the first stage and starts mapping your WIG connections. You can still change any WIG while the live review is running."
          confirmLabel="Continue to WIG Connections"
          loadingLabel="Continuing…"
          confirming={locking}
          onConfirm={handleConfirmComplete}
          onBack={() => setShowCompleteConfirm(false)}
          onClose={() => setShowCompleteConfirm(false)}
        />
      )}

      {pendingDelete && (
        <DeleteWigDialog
          item={pendingDelete}
          connectionCount={pendingDeleteConnections}
          busy={
            !!pendingItems[String(pendingDelete.execution_blueprint_item_id)]
          }
          onConfirm={handleConfirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
};

export default ExecutionBlueprintLiveReview;
